import { useTranslation } from 'react-i18next'
import {
  mapVersionToCostCalculator,
  VersionCostCalculator,
} from './VersionCostCalculator'
import { CalculatorError, PricingModelMissingError } from '../calculator-error'
import {
  PricingModelFragment,
  ProjectVersionFragment,
} from '../../generated/graphql'
import { VersionCalculator } from '../VersionCalculator'
import { useNotifications } from '../../notifications/use-notifications'

function calculateVersionCosts(
  version: ProjectVersionFragment,
  energyCalculator: VersionCalculator | null,
  pricingModel: PricingModelFragment | null,
): VersionCostCalculator {
  if (!energyCalculator) {
    throw new Error('Missing version when performing cost calculation')
  }

  try {
    if (!pricingModel) {
      throw new PricingModelMissingError(version.name)
    }

    const costCalculator = mapVersionToCostCalculator(
      energyCalculator,
      version,
      pricingModel,
    )

    const start = Date.now()
    costCalculator.calculateCosts()
    const end = Date.now()

    // eslint-disable-next-line no-console
    console.log(
      `%c✅ Cost calculation complete for ${version.id} in ${end - start} ms`,
      'color:lightgreen',
    )

    return costCalculator
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      `%c🚨 Cost calculation error for ${version.id}${
        error instanceof Error ? `: ${error.message}` : ''
      }`,
      'color:lightcoral',
    )

    throw error
  }
}

type UseCostCalculator = {
  getCalculatedCosts: (
    version: ProjectVersionFragment | null,
    energyCalculator: VersionCalculator | null,
    pricingModel: PricingModelFragment | null,
  ) => VersionCostCalculator | null
}

export function useCostCalculator(): UseCostCalculator {
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()

  return {
    getCalculatedCosts(
      version: ProjectVersionFragment | null,
      energyCalculator: VersionCalculator | null,
      pricingModel: PricingModelFragment | null,
    ): VersionCostCalculator | null {
      if (!version || !energyCalculator) {
        return null
      }
      try {
        return calculateVersionCosts(version, energyCalculator, pricingModel)
      } catch (error) {
        if (!(error instanceof CalculatorError)) {
          throw error
        }

        const translation = error.getTranslation()
        const message = t(translation.key, translation.context)

        enqueueError(t('calculator.error.base', { message }))
      }
      return null
    },
  }
}
